import { useState, useEffect } from 'react'
import { createPortal } from 'react-dom'
import Swal from 'sweetalert2'
import { useAuth } from '../hooks/useAuth'
import { useProfileRefresh } from '../hooks/useProfileRefresh'
import { walletDeposit, walletWithdraw, getUserCreditHistory } from '../services/api'
import useModalEnter from '../hooks/useModalEnter'

// Credit Activity modal — CR column number pe click se khulta hai (Users +
// ActiveUsers). Reference DOM: modal-lg, header bg-warning, 3 tabs — Credit
// Deposit / Credit Withdraw / Credit History. Deposit & Withdraw forms me
// upar login-user (parent) aur target-user ke balance (current | new projected)
// readonly dikhte hain, phir Amount / Remark / Transaction Code + submit.
function CreditActivityModal({ show, onHide, account, onSuccess }) {
  const { user } = useAuth()
  // Deposit/withdraw move money on BOTH sides, so a successful submit leaves
  // two stale readings on screen: the logged-in account's own credit (Redux
  // auth.user, which Layout otherwise only refetches on a route change) and
  // the caller's list row. Refresh both instead of waiting for a hard refresh.
  const refreshProfile = useProfileRefresh()
  const [tab, setTab] = useState('deposit') // deposit | withdraw | history
  // Open animation + outside click: hooks/useModalEnter (this file used to
  // carry its own `entered` copy of the same thing).
  const { modalClass, backdropClass, onBackdropClick } = useModalEnter(onHide, show)

  useEffect(() => { if (show) setTab('deposit') }, [show])

  // Inside loader — MoreModal jaisa: modal open pe AUR har tab switch pe 1s
  // spinner overlay, phir content. (content overlay ke neeche gated rehta hai)
  const [insideLoading, setInsideLoading] = useState(false)
  useEffect(() => {
    if (!show) return
    setInsideLoading(true)
    const t = setTimeout(() => setInsideLoading(false), 1000)
    return () => clearTimeout(t)
  }, [show, tab])

  // BootstrapVue marks the body while a modal is open (scroll-lock + styling) —
  // DepositModal/MoreModal jaisa, taaki backdrop + dialog center me ek saath aaye
  useEffect(() => {
    if (!show) return
    document.body.classList.add('modal-open')
    document.body.setAttribute('data-modal-open-count', '1')
    return () => {
      document.body.classList.remove('modal-open')
      document.body.removeAttribute('data-modal-open-count')
    }
  }, [show])

  if (!show || !account) return null

  const num = (v) => { const n = parseFloat(v); return Number.isFinite(n) ? n : 0 }

  const parentName = user?.username || 'Owner'
  const parentBal = num(user?.credit ?? user?.available_points ?? user?.inr_balance)
  const userName = account.username
  const userBal = num(account.credit ?? account.credit_ref ?? account.cash_received)
  // API target — USER → user_id, staff → staff_id
  const userId = account.user_id ?? account.staff_id ?? account.wallet_id
  const userType = account.user_type ?? account.role ?? 'USER'

  const close = () => onHide()

  // Close first so the modal doesn't hang on the network, then refetch the
  // header balance and the parent list. The API has already committed by the
  // time we get here, so both refetches read post-transfer numbers.
  // `message` = the server's success text (constants/messages.js
  // ADMIN.USERS.BALANCE_TRANSFER_SUCCESS for a deposit); the page toasts it.
  const handleSuccess = (mode, message) => {
    close()
    refreshProfile()
    onSuccess?.(mode, message)
  }

  return createPortal(
    <div style={{ position: 'absolute', zIndex: 1040 }}>
      <div role="dialog" aria-modal="true" className={modalClass} style={{ display: 'block' }} onClick={onBackdropClick}>
        <div className="modal-dialog modal-lg">
          <span tabIndex="0"></span>
          <div tabIndex="-1" className="modal-content">
            <header className="modal-header bg-warning">
              <h5 className="modal-title text-uppercase text-white">Credit Activity</h5>
              <button type="button" data-dismiss="modal" className="close text-white" onClick={close}>×</button>
            </header>
            <div className="modal-body credit-mdl">
              {insideLoading && (
                <div className="inside-loader-overlay"><i className="fas fa-spinner fa-spin"></i></div>
              )}
              <div className="tabs">
                <div className="">
                  <ul role="tablist" className="nav nav-tabs">
                    <li role="presentation" className="nav-item">
                      <a role="tab" href="#" target="_self"
                        className={`nav-link${tab === 'deposit' ? ' active tab-bg-warning' : ''}`}
                        onClick={(e) => { e.preventDefault(); setTab('deposit') }}>
                        <span>Credit Deposit</span>
                      </a>
                    </li>
                    <li role="presentation" className="nav-item">
                      <a role="tab" href="#" target="_self"
                        className={`nav-link${tab === 'withdraw' ? ' active tab-bg-warning' : ''}`}
                        onClick={(e) => { e.preventDefault(); setTab('withdraw') }}>
                        <span>Credit Withdraw</span>
                      </a>
                    </li>
                    <li role="presentation" className="nav-item">
                      <a role="tab" href="#" target="_self"
                        className={`nav-link${tab === 'history' ? ' active tab-bg-warning' : ''}`}
                        onClick={(e) => { e.preventDefault(); setTab('history') }}>
                        <span>Credit History</span>
                      </a>
                    </li>
                  </ul>
                </div>
                <div className="tab-content text-muted">
                  <div role="tabpanel" className={`tab-pane${tab === 'deposit' ? ' active' : ''}`} style={tab === 'deposit' ? {} : { display: 'none' }}>
                    {tab === 'deposit' && !insideLoading && (
                      <CreditForm mode="deposit" onDone={handleSuccess}
                        parentName={parentName} parentBal={parentBal}
                        userName={userName} userBal={userBal} userId={userId} userType={userType} />
                    )}
                  </div>
                  <div role="tabpanel" className={`tab-pane${tab === 'withdraw' ? ' active' : ''}`} style={tab === 'withdraw' ? {} : { display: 'none' }}>
                    {tab === 'withdraw' && !insideLoading && (
                      <CreditForm mode="withdraw" onDone={handleSuccess}
                        parentName={parentName} parentBal={parentBal}
                        userName={userName} userBal={userBal} userId={userId} userType={userType} />
                    )}
                  </div>
                  <div role="tabpanel" className={`tab-pane${tab === 'history' ? ' active' : ''}`} style={tab === 'history' ? {} : { display: 'none' }}>
                    {tab === 'history' && !insideLoading && <CreditHistory userId={userId} />}
                  </div>
                </div>
              </div>
            </div>
          </div>
          <span tabIndex="0"></span>
        </div>
      </div>
      <div className={backdropClass}></div>
    </div>,
    document.body,
  )
}

// Deposit / Withdraw form — mode se sirf projection direction + endpoint badalta hai.
function CreditForm({ mode, onDone, parentName, parentBal, userName, userBal, userId, userType }) {
  const [amount, setAmount] = useState('')
  const [remark, setRemark] = useState('')
  const [txnPassword, setTxnPassword] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  // Amount goes red (is-invalid) once it has been focused and left empty, or
  // Submit was pressed empty; typing a value clears it (user, 2026-09-17,
  // reference screenshots: empty → error state, "22" typed → plain field).
  const [amountTouched, setAmountTouched] = useState(false)

  const amt = parseFloat(amount) || 0
  // deposit: parent ghata, user badha | withdraw: ulta
  const parentNew = mode === 'deposit' ? parentBal - amt : parentBal + amt
  const userNew = mode === 'deposit' ? userBal + amt : userBal - amt
  const fmt = (n) => (Number.isFinite(n) ? String(Math.round(n * 100) / 100) : '')

  // Server refusals go to the reference's top-right error toast (capture
  // 2026-09-17: Credit Withdraw with amount > client balance →
  // "Your Client Does Not Have Sufficient Balance, Because Client Balance is :
  // 10006.00", modal stays open, no inline text). The text is the server's
  // (constants/messages.js ADMIN.USERS.WITHDRAW_INSUFFICIENT_BALANCE, and
  // whatever the other guards say) — nothing is worded here. The two
  // local checks above the request keep their inline red line.
  const Toast = Swal.mixin({ toast: true, position: 'top-end', showConfirmButton: false, timer: 3000, timerProgressBar: false })
  const submit = async (e) => {
    e.preventDefault()
    setError('')
    setAmountTouched(true)
    if (!amt || amt <= 0) { setError('Enter a valid amount'); return }
    if (!txnPassword) { setError('Transaction Code required'); return }
    setBusy(true)
    try {
      const payload = { userId, amount: amt, userType, remark, transactionPassword: txnPassword }
      const res = mode === 'deposit' ? await walletDeposit(payload) : await walletWithdraw(payload)
      if (res?.success) onDone(mode, res?.message)
      else Toast.fire({ icon: 'error', text: res?.message || 'Something went wrong' })
    } catch (err) {
      Toast.fire({ icon: 'error', text: err?.response?.data?.message || err?.message || 'Request failed' })
    } finally {
      setBusy(false)
    }
  }

  return (
    <form data-vv-scope="userCreditUpdate" method="post" onSubmit={submit}>
      <div className="form-group row">
        <label className="col-form-labl col-4">{parentName}</label>
        <div className="col-8"><div className="row">
          <div className="col-6"><input placeholder="Amount" type="text" readOnly value={fmt(parentBal)} name="userCreditUpdateloginusramount" className="form-control txt-right" /></div>
          {/* Projection column is ALWAYS filled — with no amount typed it
              shows the current balance (883 → 883, 10006 → 10006), as the
              reference does (capture 2026-09-17); it used to sit empty. */}
          <div className="col-6"><input placeholder="Amount" type="text" readOnly value={fmt(parentNew)} name="userCreditUpdateloginusrNamount" className="form-control txt-right" /></div>
        </div></div>
      </div>
      <div className="form-group row">
        <label className="col-form-label col-4">{userName}</label>
        <div className="col-8"><div className="row">
          <div className="col-6"><input placeholder="Amount" type="text" readOnly value={fmt(userBal)} name="userCreditUpdateusrnameamount" className="form-control txt-right" /></div>
          <div className="col-6"><input placeholder="Amount" type="text" readOnly value={fmt(userNew)} name="userCreditUpdateusrnameNamount" className="form-control txt-right" /></div>
        </div></div>
      </div>
      <div className="form-group row">
        <label className="col-form-label col-4">Amount</label>
        <div className="col-8 form-group-feedback form-group-feedback-right">
          {/* Numbers only (user, 2026-09-17 — "ssss" was getting through):
              digits and at most one dot, anything else dropped as it is typed,
              so the projection columns never see NaN. Kept as type="text" —
              the reference input is text too, and a native number input adds
              spinners and lets "e"/"-" in. */}
          <input placeholder="Amount" type="text" inputMode="decimal" value={amount}
            onChange={(e) => {
              const v = e.target.value.replace(/[^0-9.]/g, '')
              const i = v.indexOf('.')
              setAmount(i === -1 ? v : v.slice(0, i + 1) + v.slice(i + 1).replace(/\./g, ''))
            }}
            onBlur={() => setAmountTouched(true)}
            name="userCreditUpdateamount" aria-required="true"
            aria-invalid={amountTouched && !amt ? 'true' : 'false'}
            className={`form-control txt-right${amountTouched && !amt ? ' is-invalid' : ''}`} />
        </div>
      </div>
      <div className="form-group row">
        <label className="col-form-label col-4">Remark</label>
        <div className="col-8 form-group-feedback form-group-feedback-right">
          <textarea placeholder="Remark" value={remark} onChange={(e) => setRemark(e.target.value)}
            name="userCreditUpdateremark" className="form-control"></textarea>
        </div>
      </div>
      <div className="form-group row">
        <label className="col-form-label col-4">Transaction Code</label>
        <div className="col-8 form-group-feedback form-group-feedback-right">
          <input placeholder="Transaction Code" value={txnPassword} onChange={(e) => setTxnPassword(e.target.value)}
            name="userCreditUpdatempassword" type="password" className="form-control" aria-required="true" />
        </div>
      </div>
      {error && <div className="form-group row"><div className="col-12 text-right text-danger">{error}</div></div>}
      <div className="form-group row">
        <div className="col-12 text-right">
          <button type="submit" className="btn btn-warning" disabled={busy}>
            submit
            <i className="fas fa-sign-in-alt ml-1"></i>
          </button>
        </div>
      </div>
    </form>
  )
}

// Credit History tab — paginated ledger. Columns: Super User, User, Transfer From, Amount, Date.
function CreditHistory({ userId }) {
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    let alive = true
    setLoading(true)
    getUserCreditHistory({ user_id: userId, page: 1, limit: 25 })
      .then((res) => { if (alive && res?.success) setRows(res.data || []) })
      .catch(() => {})
      .finally(() => { if (alive) setLoading(false) })
    return () => { alive = false }
  }, [userId])

  // Reference: date-only (07/07/2026), no time
  const fmtDate = (d) => {
    if (!d) return ''
    const dt = new Date(d)
    if (isNaN(dt)) return String(d)
    const p = (n) => String(n).padStart(2, '0')
    return `${p(dt.getDate())}/${p(dt.getMonth() + 1)}/${dt.getFullYear()}`
  }

  return (
    <div className="table-responsive">
      <div className="table no-footer table-responsive-sm">
        <table id="eventsListTbl" role="table" aria-busy={loading ? 'true' : 'false'} aria-colcount="5" className="table b-table">
          <thead role="rowgroup">
            <tr role="row">
              <th role="columnheader" scope="col" aria-colindex="1"><div>Super User</div></th>
              <th role="columnheader" scope="col" aria-colindex="2"><div>User</div></th>
              <th role="columnheader" scope="col" aria-colindex="3"><div>Transfer From</div></th>
              <th role="columnheader" scope="col" aria-colindex="4" className="text-right"><div>Amount</div></th>
              <th role="columnheader" scope="col" aria-colindex="5"><div>Date</div></th>
            </tr>
          </thead>
          <tbody role="rowgroup">
            {rows.length === 0 ? (
              <tr role="row" className="b-table-empty-row">
                <td colSpan="5" role="cell">
                  <div role="alert" aria-live="polite">
                    <div className="text-center my-2">{loading ? 'Loading...' : 'There are no records to show'}</div>
                  </div>
                </td>
              </tr>
            ) : rows.map((r, i) => (
              <tr role="row" key={r.id || i}>
                <td aria-colindex="1" role="cell">{r.super_user || r.from_username || r.parent_username || '-'}</td>
                <td aria-colindex="2" role="cell">{r.user || r.to_username || r.username || '-'}</td>
                <td aria-colindex="3" role="cell">{r.transfer_from || r.initiated_by || r.source || '-'}</td>
                <td aria-colindex="4" role="cell"><p className="text-right mb-0">{r.amount ?? r.credit_amount ?? '-'}</p></td>
                <td aria-colindex="5" role="cell"><p className="mb-0">{fmtDate(r.created_at || r.createdAt || r.date)}</p></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

export default CreditActivityModal
